import * as Sentry from '@sentry/browser';
import {
  Pagination,
  Table,
  useTableHelpers,
  useTablePagination,
} from '@teamsnap/snap-ui';
import ExportModal from 'components/common/export-modal';
import { ReportType } from 'components/pages/financials';
import { reactiveFeedbackMessage } from 'core/apollo-cache';
import { createFilterID } from 'core/utils';
import {
  AMPLITUDE_FINANCIAL_ACTIONS,
  AMPLITUDE_REPORT_TYPE,
  useAmplitude,
} from 'hooks/amplitude';
import {
  defaultTransactionFilters,
  LoadingType,
  useFinancials,
  useFinancialsActions,
} from 'hooks/financials';
import * as React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  buildCSVFileExport,
  columnsForExportModal,
} from '../financials-exports';
import FinancialsTableFilters from '../financials-table-filters';
import FinancialsTableSkeletonLoader from '../financials-table-skeleton-loader';
import {
  beginningOfDay,
  endOfDay,
  orgDateTimeFromUTCDate,
} from '../financials-utils';
import { useAmplitudeFilterTracking } from '../hook';
import {
  DATE_RANGE_ACTIONS_OPTIONS_FOR_AMPLITUDE,
  TransactionReport,
} from '../types';
import { getColumns } from './columns';
import DepositSummary from './deposit-summary';

type FinancialsDepositsListProps = {
  setCurrentReport: (reportType: ReportType) => void;
};

export default function FinancialsDepositsList({
  setCurrentReport,
}: FinancialsDepositsListProps) {
  // State
  const [showExportModal, setShowExportModal] = useState<boolean>(false);
  // Hooks
  const { organization, depositFilters, loading, deposits } = useFinancials();
  const depositsLoading = loading[LoadingType.GetDeposits];
  const { getDeposits, updateDepositsFilter, setTransactionsFilters } =
    useFinancialsActions();
  const {
    prepareFilterForAmplitude,
    trackResultsUsingLastFilterForAmplitude,
    trackOnClearForAmplitude,
  } = useAmplitudeFilterTracking(AMPLITUDE_REPORT_TYPE.DEPOSIT);
  const { depositAccountOptions } = useGetOptionsForComboboxes(deposits);
  const { amplitudeTrack } = useAmplitude();

  useEffect(() => {
    const startDate = orgDateTimeFromUTCDate(
      organization.timezoneIana,
      beginningOfDay(depositFilters.datesRange[0])
    );
    const endDate = orgDateTimeFromUTCDate(
      organization.timezoneIana,
      endOfDay(depositFilters.datesRange[1])
    );
    getDeposits(+organization.id, 'deposit_date', startDate, endDate).catch(
      (e) => {
        // TODO: Show user error message with Toast when that is available.
        Sentry.captureException(e);
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization, depositFilters.datesRange]);

  function viewTransaction(date: Date, depositId: string) {
    amplitudeTrack.tsbFinancialActionActionClicked({
      financial_action: AMPLITUDE_FINANCIAL_ACTIONS.VIEW_TRANSACTIONS,
      financial_report: AMPLITUDE_REPORT_TYPE.DEPOSIT,
    });

    setTransactionsFilters({
      ...defaultTransactionFilters,
      dateType: 'deposit',
      datesRange: [date, date],
      searchTerm: depositId
    });
    setCurrentReport(ReportType.Transactions);
  }

  const setFilter = (key, value) => {
    updateDepositsFilter(key, value);
    prepareFilterForAmplitude(key);
    onPageChange(1);
  };

  const exportTypes = [
    { label: 'Deposits', value: 'financial_deposits_export' },
  ];

  const handleOnCaptureDateRangeAction = (
    id: keyof typeof DATE_RANGE_ACTIONS_OPTIONS_FOR_AMPLITUDE
  ) => {
    amplitudeTrack.tsbFinancialDateRangeChanged({
      financial_date_option: DATE_RANGE_ACTIONS_OPTIONS_FOR_AMPLITUDE[id],
      financial_report: AMPLITUDE_REPORT_TYPE.DEPOSIT,
    });
  };

  const handleExport = (exportColumns, exportType, templateName = '') => {
    try {
      buildCSVFileExport(parsedData, exportColumns, exportType, templateName);
      setShowExportModal(false);
      amplitudeTrack.tsbFinancialsExported(AMPLITUDE_REPORT_TYPE.DEPOSIT);
    } catch (error) {
      reactiveFeedbackMessage({
        message:
          'Oops! Export could not be created. Please try again or contact TeamSnap support for help.',
        isError: true,
        autoclose: true,
      });
    }
  };

  // --- Table data ---

  function useGetOptionsForComboboxes(
    transactionDepositList: TransactionReport[]
  ) {
    const depositAccountOptions = useMemo(() => {
      if (!transactionDepositList) return null;
      const depositAccounts = transactionDepositList.map(
        (row) => row.merchantAccountDisplayName
      );
      return getFilteredAndSortedValueAndLabelFromList({
        list: depositAccounts,
        emptyLabel: 'No deposit account',
      });
    }, [transactionDepositList]);

    return {
      depositAccountOptions,
    };
  }

  function getFilteredAndSortedValueAndLabelFromList({
    list,
    emptyLabel = '-',
  }: {
    list: string[];
    emptyLabel: string;
  }) {
    return list
      .filter((value, index) => list.indexOf(value) === index)
      .sort((x, y) => (x > y ? 1 : -1))
      .map((value) => ({
        label: value || emptyLabel,
        value: value ? createFilterID(value) : null,
      }));
  }

  const getParsedData = useCallback(() => {
    if (!deposits.length) return [];

    let dataToReturn = deposits;

    dataToReturn = dataToReturn.filter(
      (x) =>
        depositFilters.depositAccount.length === 0 ||
        (x.merchantAccountDisplayName
          ? depositFilters.depositAccount.includes(
              createFilterID(x.merchantAccountDisplayName)
            )
          : depositFilters.depositAccount.includes(null)) // this corresponds to No deposit acount filter
    );

    return dataToReturn.map((row) => ({
      ...row,
      depositDate: new Date(
        orgDateTimeFromUTCDate(
          organization.timezoneIana,
          new Date(row.depositDate)
        )
      ),
      depositAccount: row.merchantAccountDisplayName,
    }));
  }, [deposits, depositFilters.depositAccount, organization.timezoneIana]);

  // NOTE: Main useMemo to parse and filter data
  const parsedData = useMemo(
    () => getParsedData(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [deposits, depositFilters.depositAccount]
  );
  const columns = getColumns(viewTransaction);
  const { rows, headerRow } = useTableHelpers({
    data: parsedData,
    columns: { ...columns },
    initialSorting: {
      method: 'date',
      sortKey: 'depositDate',
      acsending: false,
    },
  });

  const { currentPage, onPageChange, totalCount, currentRows, pageSize } =
    useTablePagination({
      rows,
      pageSize: 50,
    });

  trackResultsUsingLastFilterForAmplitude(rows.length);

  return (
    <div className="sui-py-4" data-testid="deposit-list__table">
      <DepositSummary />
      <FinancialsTableFilters
        filters={depositFilters}
        setFilter={setFilter}
        onClearAll={() => {
          trackOnClearForAmplitude();
          updateDepositsFilter('depositAccount', []);
        }}
        comboboxFilters={[
          {
            label: 'Deposit Account',
            key: 'depositAccount',
            options: depositAccountOptions,
          },
        ]}
        onExport={() => setShowExportModal(true)}
        onCaptureDateRangeAction={handleOnCaptureDateRangeAction}
      />
      {depositsLoading ? (
        <FinancialsTableSkeletonLoader />
      ) : (
        <>
          <div className="sui-overflow-x-auto sui-mb-2">
            <div style={{ width: 'max-content', minWidth: '100%' }}>
              <Table headerRow={headerRow} rows={currentRows} />
            </div>
          </div>
          <ExportModal
            columns={columnsForExportModal(columns)}
            title="Generate Deposits Export"
            show={showExportModal}
            organizationId={+organization.id}
            exportTypes={exportTypes}
            onClose={() => setShowExportModal(false)}
            onSubmit={handleExport}
          />
          <Pagination
            {...{ currentPage, onPageChange, totalCount, pageSize }}
          />
        </>
      )}
    </div>
  );
}
